import QuickRedoPanel from './QuickRedoPanel';
import TestStepsButton from './TestStepsButton';
import { previewPdf } from './PdfPreview';
import { createPortal } from 'react-dom';
/**
 * SavedFTCReportsGallery
 * Lists previously-saved FTC Identity Theft Reports so the user can reopen,
 * edit, and regenerate them. Shown at the top of the FTC Report Builder.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  FileClock, FolderOpen, Trash2, Search, AlertCircle, RotateCcw, ChevronDown, ChevronUp, Loader2, Mail, Send, Clock,
  FileText, Edit3, Download, Repeat2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const fmtMoney = (n) => {
  const v = Number(n || 0);
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

export default function SavedFTCReportsGallery({ onOpenReport, onStartNew }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [filter, setFilter] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  // Multi-select for bulk delete of previous clients (user request Feb 2026).
  // `selectedIds` is a Set of report ids currently checked.  When non-empty
  // the toolbar swaps in a "Delete N selected" button so the user can wipe
  // multiple old clients in one action.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Credit Repair Steps status per report: { [reportId]: {status, sent_at, delivery_choice, scheduled_at} }
  const [crsStatusByReport, setCrsStatusByReport] = useState({});
  const [sendingCrsForId, setSendingCrsForId] = useState(null);
  // Confirmation modal state for the "Send Credit Repair Steps" button.
  // Shows the original email it was sent to, allows editing it, and supports
  // sending to multiple addresses (comma- or newline-separated).
  const [crsModalReport, setCrsModalReport] = useState(null);   // {id, client_name, client_email}
  const [crsModalEmails, setCrsModalEmails] = useState('');
  const [crsModalResults, setCrsModalResults] = useState(null); // array from backend per-recipient

  // ── Feb 2026 — Saved Dispute Letters per client (user request) ──
  // Keyed on `client_name` because saved letters store client_name (not
  // report_id).  Each row includes the collapsed list of dispute letters
  // and provides Edit / Download / Delete actions inline so the user
  // doesn't have to reopen the FTC builder just to manage them.
  const [disputeLettersByClient, setDisputeLettersByClient] = useState({});
  const [downloadingLetterId, setDownloadingLetterId] = useState(null);

  const fetchDisputeLettersFor = async (reportList) => {
    try {
      const uniqueNames = Array.from(new Set(
        reportList
          .map((r) => (r.client_name || '').trim())
          .filter((n) => n.length > 0),
      ));
      const results = await Promise.all(
        uniqueNames.map(async (name) => {
          try {
            const res = await axios.get(`${API}/api/saved-dispute-letters`, {
              params: { client_name: name },
              timeout: 10000,
            });
            return [name.toLowerCase(), res.data?.items || []];
          } catch {
            return [name.toLowerCase(), []];
          }
        }),
      );
      setDisputeLettersByClient(Object.fromEntries(results));
    } catch (_e) { /* swallow */ }
  };

  const downloadSavedLetter = async (letter) => {
    setDownloadingLetterId(letter.id);
    try {
      const res = await axios.post(
        `${API}/api/dispute-letter/generate`,
        {
          client_name: letter.client_name,
          client_address_lines: letter.client_address_lines || [],
          bureau: letter.bureau,
          bureaus_to_show: letter.bureaus_to_show || null,
          accounts: letter.accounts || [],
          letter_type: letter.letter_type || 'regular',
          subject: letter.subject || null,
          pre_body: letter.pre_body || null,
          post_body: letter.post_body || null,
          signature_font: letter.signature_font || 'cursive_elegant',
          body_font: letter.body_font || 'times',
          style: letter.style || 'typed',
          handwriting_style: letter.handwriting_style || null,
          handwriting_finish: letter.handwriting_finish || null,
          letter_date: letter.letter_date || null,
          include_sworn_statement: false,
        },
        { responseType: 'blob', timeout: 240000 },
      );
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match?.[1] || `dispute_letter_${letter.bureau}.pdf`;
      if (!(await previewPdf(res.data, filename))) return;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded — ${filename}`);
    } catch (e) {
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || text;
        } catch { /* ignore */ }
      }
      toast.error(`Download failed: ${msg}`);
    } finally {
      setDownloadingLetterId(null);
    }
  };

  const deleteSavedLetter = async (letterId, clientName) => {
    if (!window.confirm('Delete this saved dispute letter? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/saved-dispute-letters/${letterId}`);
      setDisputeLettersByClient((prev) => {
        const key = (clientName || '').toLowerCase();
        return {
          ...prev,
          [key]: (prev[key] || []).filter((l) => l.id !== letterId),
        };
      });
      toast.success('Saved letter removed');
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Feb 2026 — convert a saved letter (typically Regular) into a new
  // Second-Round Verified Account letter (or ChexSystems / Inquiry
  // Removal) without redoing the wizard.  The backend clones the source
  // with a fresh letter_type and resets subject/pre_body/post_body so
  // the correct template is used.  Result is inserted into the panel.
  const convertSavedLetter = async (letter, newType, clientName) => {
    const label = newType === 'second_round' ? 'Second Round Verified Account'
      : newType === 'inquiry_removal' ? 'Inquiry Removal'
      : newType === 'chexsystems' ? 'ChexSystems'
      : 'Regular';
    if (!window.confirm(
      `Convert this ${letter.letter_type === 'regular' ? 'Regular' : letter.letter_type} letter into a ` +
      `new "${label}" letter?  The original stays saved.`,
    )) return;
    try {
      const res = await axios.post(
        `${API}/api/saved-dispute-letters/${letter.id}/convert`,
        { letter_type: newType },
        { timeout: 15000 },
      );
      const clone = res.data;
      setDisputeLettersByClient((prev) => {
        const key = (clientName || '').toLowerCase();
        return {
          ...prev,
          [key]: [clone, ...(prev[key] || [])],
        };
      });
      toast.success(`Converted → ${label}. Open the report or use ▼ Download to render the PDF.`);
    } catch (e) {
      toast.error(`Convert failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const fetchCrsStatuses = async (reportList) => {
    try {
      const results = await Promise.all(
        reportList.map(r =>
          axios.get(`${API}/api/ftc-reports/${r.id}/credit-repair-status`)
            .then(res => [r.id, res.data])
            .catch(() => [r.id, { exists: false, status: 'none' }])
        )
      );
      setCrsStatusByReport(Object.fromEntries(results));
    } catch (_e) { /* swallow */ }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/api/ftc-reports`);
      const list = Array.isArray(data) ? data : [];
      setReports(list);
      // Fire-and-forget the credit-repair status lookups so each card knows
      // whether to show "Send Credit Repair Steps" vs "Resend Credit Repair Steps".
      fetchCrsStatuses(list);
      // Feb 2026 — Also load saved dispute letters per client so each
      // card can render its own inline "Saved Dispute Letters" panel.
      fetchDisputeLettersFor(list);
    } catch (err) {
      console.error('Failed to load saved FTC reports', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete the saved FTC report for "${name || 'this client'}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/api/ftc-reports/${id}`);
      setReports((prev) => prev.filter((r) => r.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success('Report deleted');
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = (visibleIds) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      // If every visible row is already selected → clear; else add all.
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(
      `Delete ${ids.length} saved FTC report${ids.length === 1 ? '' : 's'} for the selected client${ids.length === 1 ? '' : 's'}?  This cannot be undone.`,
    )) return;
    setBulkDeleting(true);
    let deleted = 0;
    const failures = [];
    // Run deletes in parallel (small batches ok — this is user-initiated).
    const results = await Promise.allSettled(
      ids.map((id) => axios.delete(`${API}/api/ftc-reports/${id}`)),
    );
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') deleted += 1;
      else failures.push(ids[i]);
    });
    setReports((prev) => prev.filter((r) => !ids.includes(r.id) || failures.includes(r.id)));
    setSelectedIds(new Set(failures));  // keep the ones that failed selected
    setBulkDeleting(false);
    if (failures.length) {
      toast.error(`Deleted ${deleted} of ${ids.length}. ${failures.length} failed — try again.`);
    } else {
      toast.success(`Deleted ${deleted} report${deleted === 1 ? '' : 's'}`);
    }
  };

  // Send (or resend) the Credit Repair Steps email for a saved FTC report.
  // User request 2026-03-01: instead of firing immediately, open a confirmation
  // modal pre-filled with the original recipient email so the user can edit
  // it, add additional recipients (comma-separated), and confirm before send.
  const openCrsConfirm = (report) => {
    const status = crsStatusByReport[report.id] || {};
    // Prefer the most-recently-used override if we have one, else the report's
    // stored email.  This lets the user resend to the same alternate address
    // they used last time without re-typing.
    const lastUsed = status.client_email || status.last_recipient || '';
    setCrsModalReport({ id: report.id, client_name: report.client_name, client_email: report.client_email });
    setCrsModalEmails((lastUsed || report.client_email || '').trim());
    setCrsModalResults(null);
  };

  const handleConfirmSendCreditRepairSteps = async () => {
    if (!crsModalReport) return;
    const reportId = crsModalReport.id;
    const clientName = crsModalReport.client_name;
    // Split on commas/newlines/spaces, then defensively strip any internal
    // whitespace within each piece so a typo like "REGGIE SIBLEY2050@gmail.com"
    // becomes "REGGIESIBLEY2050@gmail.com" instead of failing SMTP later.
    const recipients = (crsModalEmails || '')
      .split(/[\n,;]+/)
      .map(s => s.replace(/\s+/g, '').trim())
      .filter(Boolean);
    if (recipients.length === 0) {
      toast.error('Add at least one email address.');
      return;
    }
    // Client-side validation so the user catches typos before the round-trip.
    const bad = recipients.find(e => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (bad) {
      toast.error(`Invalid email: ${bad}`);
      return;
    }
    setSendingCrsForId(reportId);
    try {
      const { data } = await axios.post(
        `${API}/api/ftc-reports/${reportId}/send-credit-repair-now`,
        { recipient_emails: recipients },
      );
      if (data?.success) {
        const ok = data.sent_count ?? recipients.length;
        const total = recipients.length;
        toast.success(`🚀 5-email Credit Repair series started for ${ok}/${total} recipient${total === 1 ? '' : 's'} (${clientName || 'client'}). Emails send one minute apart.`);
        setCrsStatusByReport((prev) => ({
          ...prev,
          [reportId]: { exists: true, status: 'sent', sent_at: data.sent_at, delivery_choice: 'resend', client_email: recipients[0] },
        }));
        setCrsModalResults(data.recipients || null);
        // Auto-close on full success after a short delay so the user sees the result
        if (data.failed_count === 0) {
          setTimeout(() => { setCrsModalReport(null); setCrsModalResults(null); }, 1500);
        }
      } else {
        throw new Error(data?.error || 'Send failed');
      }
    } catch (err) {
      console.error('Credit Repair Steps send failed:', err);
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingCrsForId(null);
    }
  };



  const filtered = reports.filter((r) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (r.client_name || '').toLowerCase().includes(q) ||
           (r.client_email || '').toLowerCase().includes(q) ||
           (r.report_number || '').toLowerCase().includes(q);
  });

  return (
    <div className="mb-6 border border-zinc-700 rounded-xl bg-zinc-900/50 backdrop-blur-sm" data-testid="saved-ftc-gallery">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-800/30 transition-colors rounded-t-xl"
        data-testid="saved-ftc-gallery-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <FileClock className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <div className="font-semibold text-white flex items-center gap-2">
              Previous FTC Reports
              {reports.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  {reports.length}
                </span>
              )}
            </div>
            <div className="text-xs text-zinc-400">
              {reports.length === 0
                ? 'Saved reports will appear here after you finish one'
                : 'Reopen any report to edit details and regenerate the PDF'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onStartNew && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onStartNew(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onStartNew(); } }}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 cursor-pointer transition-colors"
              data-testid="start-new-ftc-btn"
            >
              + Start new report
            </span>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
        </div>
      </button>

      {/* Body */}
      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-3">
          {/* Toolbar */}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search by client name, email, or report #…"
                className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500/60"
                data-testid="saved-ftc-search"
              />
            </div>
            {selectedIds.size > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={bulkDeleting}
                  className="border-zinc-700 hover:bg-zinc-800 text-zinc-300"
                  data-testid="saved-ftc-clear-selection"
                >
                  Clear ({selectedIds.size})
                </Button>
                <Button
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="saved-ftc-bulk-delete"
                >
                  {bulkDeleting ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Deleting…</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-1.5" />Delete {selectedIds.size} selected</>
                  )}
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-zinc-700 hover:bg-zinc-800" data-testid="saved-ftc-refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              <span className="ml-1.5 hidden sm:inline">Refresh</span>
            </Button>
          </div>

          {/* States */}
          {loading && (
            <div className="py-8 flex items-center justify-center text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading saved reports…
            </div>
          )}
          {!loading && error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2 text-sm text-red-300">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="py-10 text-center text-zinc-500 text-sm">
              {reports.length === 0
                ? 'No saved reports yet. Finish and save one to see it here.'
                : 'No reports match your search.'}
            </div>
          )}

          {/* List */}
          {!loading && !error && filtered.length > 0 && (
            <>
              {/* Select-all bar — makes bulk operations easy for large lists. */}
              <div className="flex items-center gap-2 text-xs text-zinc-400 pb-1">
                <label className="inline-flex items-center gap-1.5 cursor-pointer hover:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id))}
                    onChange={() => selectAllVisible(filtered.map((r) => r.id))}
                    className="rounded border-zinc-600 bg-zinc-800 text-red-500 focus:ring-red-500 focus:ring-offset-zinc-900"
                    data-testid="saved-ftc-select-all"
                  />
                  <span>Select all visible ({filtered.length})</span>
                </label>
              </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filtered.map((r) => (
                <div
                  key={r.id}
                  className={`group bg-zinc-900 border rounded-lg p-4 transition-colors ${
                    selectedIds.has(r.id)
                      ? 'border-red-500/60 ring-1 ring-red-500/30'
                      : 'border-zinc-800 hover:border-blue-500/40'
                  }`}
                  data-testid={`saved-ftc-card-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelected(r.id)}
                        className="mt-1 rounded border-zinc-600 bg-zinc-800 text-red-500 focus:ring-red-500 focus:ring-offset-zinc-900 cursor-pointer"
                        data-testid={`saved-ftc-select-${r.id}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-white truncate">
                          {r.client_name || 'Unnamed client'}
                        </div>
                        {r.client_email && (
                          <div className="text-xs text-zinc-400 truncate">{r.client_email}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleDelete(r.id, r.client_name)}
                        disabled={deletingId === r.id || bulkDeleting}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Delete"
                        data-testid={`saved-ftc-delete-${r.id}`}
                      >
                        {deletingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                    <div className="bg-zinc-800/60 rounded px-2 py-1.5">
                      <div className="text-zinc-500">Sections</div>
                      <div className="text-white font-semibold">{r.section_count || 0}</div>
                    </div>
                    <div className="bg-zinc-800/60 rounded px-2 py-1.5">
                      <div className="text-zinc-500">Accounts</div>
                      <div className="text-white font-semibold">{r.account_count || 0}</div>
                    </div>
                    <div className="bg-zinc-800/60 rounded px-2 py-1.5">
                      <div className="text-zinc-500">Total fraud</div>
                      <div className="text-white font-semibold">{fmtMoney(r.total_fraud_amount)}</div>
                    </div>
                  </div>

                  {Number(r.outstanding_balance) > 0 && (
                    <div className="mb-3 px-2.5 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300 flex items-center justify-between">
                      <span>Outstanding balance</span>
                      <span className="font-semibold">{fmtMoney(r.outstanding_balance)}</span>
                    </div>
                  )}

                  <div className="text-[11px] text-zinc-500 mb-3">
                    Updated {fmtDate(r.updated_at)}
                    {r.report_number ? ` · Report #${r.report_number}` : ''}
                  </div>

                  <Button
                    onClick={() => onOpenReport && onOpenReport(r.id)}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid={`saved-ftc-open-${r.id}`}
                  >
                    <FolderOpen className="w-4 h-4 mr-1.5" />
                    Open &amp; edit
                  </Button>

                  {/* Credit Repair Steps — Send / Resend / Pending pill */}
                  {(() => {
                    const crs = crsStatusByReport[r.id];
                    const status = crs?.status || 'none';
                    const isPending = status === 'pending' || status === 'pending_manual';
                    const isSent = status === 'sent';
                    const isSending = sendingCrsForId === r.id;
                    const canSend = !!r.client_email;
                    const label = isPending
                      ? 'Send Credit Repair Steps'
                      : (isSent ? 'Resend Credit Repair Steps' : 'Send Credit Repair Steps');
                    const colorClass = isPending
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : (isSent ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'bg-blue-700 hover:bg-blue-800 text-white');
                    return (
                      <div className="mt-2 space-y-1.5">
                        {isPending && (
                          <div className="flex items-center gap-1.5 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1">
                            <Clock className="w-3 h-3" />
                            <span>
                              {crs?.delivery_choice === 'manual'
                                ? 'Pending — waiting on you to send'
                                : `Scheduled for ${fmtDate(crs?.scheduled_at)}`}
                            </span>
                          </div>
                        )}
                        {isSent && (
                          <div className="flex items-center gap-1.5 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1">
                            <Mail className="w-3 h-3" />
                            <span>Sent {fmtDate(crs?.sent_at)}</span>
                          </div>
                        )}
                        <Button
                          onClick={() => openCrsConfirm(r)}
                          disabled={isSending}
                          size="sm"
                          className={`w-full ${colorClass}`}
                          data-testid={`saved-ftc-send-crs-${r.id}`}
                          title={canSend ? label : 'No email on file — you can still type one in the next popup'}
                        >
                          {isSending
                          ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          : <Send className="w-4 h-4 mr-1.5" />}
                        {label}
                      </Button>
                    </div>
                  );
                })()}

                <QuickRedoPanel report={r} letters={disputeLettersByClient[(r.client_name || '').toLowerCase()] || []} onChanged={() => { fetchDisputeLettersFor(reports); load(); }} />

                {/* ── Feb 2026 — Saved Dispute Letters for this client ──
                    Renders inline right under Send Credit Repair Steps so
                    the user can Edit / Download / Delete without opening
                    the full FTC builder. */}
                {(() => {
                  const key = (r.client_name || '').toLowerCase();
                  const letters = disputeLettersByClient[key] || [];
                  if (letters.length === 0) return null;
                  return (
                    <div
                      className="mt-3 pt-3 border-t border-zinc-800 space-y-2"
                      data-testid={`saved-dispute-letters-panel-${r.id}`}
                    >
                      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-fuchsia-300">
                        <FileText className="w-3.5 h-3.5" />
                        Saved Dispute Letters ({letters.length})
                      </div>
                      <div className="space-y-1.5">
                        {letters.map((l) => {
                          const typeLabel = l.letter_type === 'second_round'
                            ? 'Second Round'
                            : l.letter_type === 'third_round'
                              ? 'Final Demand'
                            : l.letter_type === 'inquiry_removal'
                              ? 'Inquiry Removal'
                              : l.letter_type === 'chexsystems'
                                ? 'ChexSystems'
                                : l.letter_type === 'business'
                                  ? 'Business'
                                  : 'Regular';
                          const isDownloading = downloadingLetterId === l.id;
                          return (
                            <div
                              key={l.id}
                              className="flex items-center justify-between gap-2 bg-zinc-800/60 border border-zinc-700 rounded-md p-2"
                              data-testid={`saved-dispute-letter-${l.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-[12px] text-white font-semibold truncate">
                                  {typeLabel} · {String(l.bureau).toUpperCase()}
                                </div>
                                <div className="text-[10px] text-zinc-400 truncate">
                                  {(l.accounts?.length || 0)} acct{(l.accounts?.length || 0) === 1 ? '' : 's'}
                                  {' · '}{l.body_font || 'times'}
                                  {' · '}{l.letter_date || (l.updated_at ? new Date(l.updated_at).toLocaleDateString() : '—')}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {/* Convert to another letter type — shows
                                    a native <select> dropdown so the user
                                    can turn a Regular into Second Round /
                                    Inquiry Removal / ChexSystems without
                                    redoing the wizard. */}
                                <div className="relative">
                                  <select
                                    value=""
                                    onChange={(e) => {
                                      const target = e.target.value;
                                      e.target.value = '';
                                      if (!target) return;
                                      convertSavedLetter(l, target, r.client_name);
                                    }}
                                    className="h-7 pl-6 pr-1 text-[10px] uppercase tracking-wider font-bold rounded-md bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/40 text-amber-200 outline-none appearance-none cursor-pointer"
                                    title="Convert this letter into another type"
                                    data-testid={`gallery-letter-convert-${l.id}`}
                                  >
                                    <option value="" disabled hidden>→</option>
                                    {l.letter_type !== 'regular' && (
                                      <option value="regular">Regular Dispute</option>
                                    )}
                                    {l.letter_type !== 'second_round' && (
                                      <option value="second_round">Second Round / Verified Account</option>
                                    )}
                                    {l.letter_type !== 'inquiry_removal' && (
                                      <option value="inquiry_removal">Inquiry Removal (FCRA §604)</option>
                                    )}
                                    {l.letter_type !== 'chexsystems' && (
                                      <option value="chexsystems">ChexSystems ID Theft</option>
                                    )}
                                  </select>
                                  <Repeat2 className="w-3.5 h-3.5 absolute left-1 top-1/2 -translate-y-1/2 text-amber-200 pointer-events-none" />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => onOpenReport && onOpenReport(r.id)}
                                  className="h-7 w-7 flex items-center justify-center rounded-md bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border border-fuchsia-400/40 text-fuchsia-200"
                                  title="Open FTC report to edit this letter"
                                  data-testid={`gallery-letter-edit-${l.id}`}
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => downloadSavedLetter(l)}
                                  disabled={isDownloading}
                                  className="h-7 w-7 flex items-center justify-center rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-200 disabled:opacity-50"
                                  title="Re-download this letter"
                                  data-testid={`gallery-letter-download-${l.id}`}
                                >
                                  {isDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteSavedLetter(l.id, r.client_name)}
                                  className="h-7 w-7 flex items-center justify-center rounded-md bg-red-500/15 hover:bg-red-500/25 border border-red-400/40 text-red-300"
                                  title="Delete this saved letter"
                                  data-testid={`gallery-letter-delete-${l.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          Confirm Send modal (per user request 2026-03-01)
          - Shows the original email this report was sent to.
          - User can edit it, add commas/newlines to send to multiple addresses.
          - After send, the per-recipient results are surfaced inline.
          ════════════════════════════════════════════════════════════════ */}
      {crsModalReport && createPortal(
        <div className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3" data-testid="crs-confirm-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md shadow-2xl flex flex-col" style={{ maxHeight: 'calc(100dvh - 1.5rem)' }}>
            <div className="px-5 py-4 border-b border-zinc-700 bg-gradient-to-r from-emerald-700 to-cyan-700">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-emerald-100 text-[10px] uppercase font-bold tracking-widest">Send Credit Repair Steps</p>
                  <h3 className="text-white font-bold text-base truncate">{crsModalReport.client_name || 'Unnamed Report'}</h3>
                </div>
                <button
                  onClick={() => { setCrsModalReport(null); setCrsModalResults(null); }}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/20"
                  aria-label="Close"
                  data-testid="crs-confirm-close"
                >
                  <span className="text-white text-xl leading-none">×</span>
                </button>
              </div>
            </div>

            <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="rounded-lg bg-zinc-800/60 border border-zinc-700 p-3">
                <p className="text-zinc-400 text-[10px] uppercase tracking-wide font-bold mb-1">Originally sent to</p>
                <p className="text-zinc-100 text-sm break-all">{crsModalReport.client_email || <span className="text-zinc-500 italic">— no email on file —</span>}</p>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-1.5">Recipient(s)</label>
                <textarea
                  value={crsModalEmails}
                  onChange={(e) => setCrsModalEmails(e.target.value)}
                  rows={3}
                  placeholder="client@example.com, friend@example.com"
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                  data-testid="crs-confirm-emails"
                  autoFocus
                />
                <p className="text-[11px] text-zinc-500 mt-1">Separate multiple addresses with commas, semicolons or new lines. The same email is sent to each address.</p>
              </div>

              {crsModalResults && (
                <div className="space-y-1.5" data-testid="crs-confirm-results">
                  <p className="text-zinc-300 text-xs font-semibold">Results</p>
                  {crsModalResults.map((r, i) => (
                    <div key={i} className={`flex items-center justify-between rounded-md px-2.5 py-1.5 text-[12px] border ${r.success ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-200' : 'bg-red-950/30 border-red-500/40 text-red-200'}`}>
                      <span className="truncate min-w-0 mr-2">{r.email}</span>
                      <span className="flex-shrink-0 font-semibold">{r.success ? '✓ Sent' : `✗ ${r.error || 'Failed'}`}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <TestStepsButton reportId={crsModalReport.id} clientName={crsModalReport.client_name} />
            <div className="grid grid-cols-2 gap-2 p-4 border-t border-zinc-700 bg-zinc-900/60">
              <button
                onClick={() => { setCrsModalReport(null); setCrsModalResults(null); }}
                disabled={sendingCrsForId === crsModalReport.id}
                className="px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                data-testid="crs-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendCreditRepairSteps}
                disabled={sendingCrsForId === crsModalReport.id || !crsModalEmails.trim()}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="crs-confirm-send"
              >
                {sendingCrsForId === crsModalReport.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
